import { useEffect, useEffectEvent, useMemo, useState } from 'react';
import { browserLabel } from '@/lib/browser-context';
import type { StateSetter } from '@/lib/desktop-shell/store';
import { type NativeBrowserUseActivity, readNativeBrowserUseActivity } from '@/lib/native-desktop';
import {
  type BrowserSession,
  type BrowserSessionMode,
  createBrowserSession,
  createBrowserSessionAt,
} from '@/lib/side-panel/browser-session';
import {
  activatePanelTab,
  lastPanelTabOf,
  openPanelTab,
  type PanelTabs,
  panelTabsOf,
} from '@/lib/side-panel/panel-tabs';
import type { SidePanelView } from '@/lib/side-panel/side-panel-items';

function browserActivityId(activity: NativeBrowserUseActivity): string {
  const prefix = 'crowdex-browser-';
  return (
    activity.id ??
    (activity.webviewLabel.startsWith(prefix) ? activity.webviewLabel.slice(prefix.length) : '')
  );
}

const NO_RETAINED_TABS: ReadonlySet<string> = new Set();

/**
 * The browser tabs of the side panel, one per tab of the browser view, and the Desktop's
 * requests about them (an agent opening, showing or closing a tab). A browser tab shown without
 * a page yet opens the default one.
 */
export function useBrowserTabs({
  nativeReady,
  onCloseTab,
  onReveal,
  panelView,
  retainedTabIds = NO_RETAINED_TABS,
  setTabs,
  tabs,
  workspace,
}: {
  /** Tabs open in other conversations' panels: their pages are kept for when they show again. */
  retainedTabIds?: ReadonlySet<string>;
  nativeReady: boolean;
  /** Closes a tab of the side panel (the panel itself with its last tab). */
  onCloseTab: (id: string) => void;
  /** Opens the side panel on what it shows. */
  onReveal: () => void;
  /** The browser view, once the workbench declaring it has loaded. */
  panelView: SidePanelView | undefined;
  setTabs: StateSetter<PanelTabs>;
  tabs: PanelTabs;
  workspace: string | null;
}) {
  const [storedSessions, setStoredSessions] = useState<readonly BrowserSession[]>([]);
  const [controlledBrowserIds, setControlledBrowserIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const viewTabs = useMemo(
    () => (panelView === undefined ? [] : panelTabsOf(tabs, panelView)),
    [panelView, tabs],
  );
  const browserSessions = useMemo(
    () => viewTabs.flatMap((tab) => storedSessions.filter((session) => session.id === tab.id)),
    [storedSessions, viewTabs],
  );
  const activeBrowserId =
    panelView === undefined ? '' : (lastPanelTabOf(tabs, panelView)?.id ?? '');

  const missingIds = viewTabs
    .filter((tab) => !storedSessions.some((session) => session.id === tab.id))
    .map((tab) => tab.id)
    .join('\n');
  useEffect(() => {
    if (missingIds.length === 0) return;
    const ids = missingIds.split('\n');
    setStoredSessions((current) => [
      ...current,
      ...ids
        .filter((id) => !current.some((session) => session.id === id))
        .map((id) => ({ ...createBrowserSession(), id })),
    ]);
  }, [missingIds]);

  // A closed tab forgets its page.
  const openIds = viewTabs.map((tab) => tab.id).join('\n');
  useEffect(() => {
    if (panelView === undefined) return;
    const open = new Set(openIds.split('\n'));
    setStoredSessions((current) => {
      const kept = current.filter(
        (session) => open.has(session.id) || retainedTabIds.has(session.id),
      );
      return kept.length === current.length ? current : kept;
    });
  }, [openIds, panelView, retainedTabIds]);

  function openSession(session: BrowserSession): void {
    if (panelView === undefined) return;
    setStoredSessions((current) =>
      current.some((candidate) => candidate.id === session.id) ? current : [...current, session],
    );
    setTabs((current) => openPanelTab(current, { id: session.id, view: panelView }));
  }

  function addBrowserTab(mode: BrowserSessionMode = 'shared', url?: string): void {
    openSession(
      url === undefined
        ? createBrowserSession(mode)
        : createBrowserSessionAt(browserLabel(url), url, { mode }),
    );
  }

  function updateBrowserSession(id: string, update: (session: BrowserSession) => BrowserSession) {
    setStoredSessions((current) =>
      current.map((session) => (session.id === id ? update(session) : session)),
    );
  }

  // The Desktop's requests read the panel as it is when they arrive. They are effect events so
  // that the subscription below stays for good: re-subscribing on every render (a conversation
  // re-renders on each chunk an agent streams) lost the requests sent in between.
  const activateTab = useEffectEvent(({ id }: { id: string }) => {
    setTabs((current) => activatePanelTab(current, id));
    onReveal();
  });
  /** An agent's new tab, in the profile it asked for (checked by the browser runtime). */
  const openAgentTab = useEffectEvent(
    ({
      id,
      url,
      session,
      profile,
    }: {
      id: string;
      url: string;
      session: BrowserSessionMode;
      profile?: string;
    }) => {
      openSession(createBrowserSessionAt('Browser', url, { id, mode: session, profile }));
      onReveal();
    },
  );
  const closeAgentTab = useEffectEvent(({ id }: { id: string }) => {
    setControlledBrowserIds((current) => {
      if (!current.has(id)) return current;
      const updated = new Set(current);
      updated.delete(id);
      return updated;
    });
    if (tabs.tabs.some((tab) => tab.id === id)) onCloseTab(id);
  });

  useEffect(() => {
    if (!nativeReady) return;
    let disposed = false;
    let unlisten: (() => void) | null = null;
    void import('@/lib/electron-bridge').then(async ({ listen }) => {
      const stops = await Promise.all([
        listen<Parameters<typeof activateTab>[0]>('browser-activate-tab-request', ({ payload }) =>
          activateTab(payload),
        ),
        listen<Parameters<typeof openAgentTab>[0]>('browser-new-tab-request', ({ payload }) =>
          openAgentTab(payload),
        ),
        listen<Parameters<typeof closeAgentTab>[0]>('browser-close-tab-request', ({ payload }) =>
          closeAgentTab(payload),
        ),
      ]);
      const stop = () => {
        for (const cleanup of stops) cleanup();
      };
      if (disposed) stop();
      else unlisten = stop;
    });
    return () => {
      disposed = true;
      unlisten?.();
    };
  }, [nativeReady]);

  // biome-ignore lint/correctness/useExhaustiveDependencies(workspace): another workspace reads which browsers the agent controls again, from a fresh snapshot.
  useEffect(() => {
    if (!nativeReady) {
      setControlledBrowserIds(new Set());
      return;
    }
    let disposed = false;
    let unlisten: (() => void) | null = null;
    void import('@/lib/electron-bridge').then(async ({ listen }) => {
      let hydrated = false;
      const pending: NativeBrowserUseActivity[] = [];
      const applyActivity = (payload: NativeBrowserUseActivity) => {
        const id = browserActivityId(payload);
        if (id.length === 0) return;
        setControlledBrowserIds((current) => {
          const next = new Set(current);
          if (payload.active) next.add(id);
          else next.delete(id);
          return next;
        });
      };
      const stop = await listen<NativeBrowserUseActivity>(
        'browser-agent-activity-changed',
        ({ payload }) => {
          if (!hydrated) pending.push(payload);
          else applyActivity(payload);
        },
      );
      if (disposed) {
        stop();
        return;
      }
      let snapshot: NativeBrowserUseActivity[] = [];
      try {
        snapshot = await readNativeBrowserUseActivity();
      } catch {
        // Keep the live subscription useful if snapshot hydration is unavailable.
      }
      if (disposed) {
        stop();
        return;
      }
      setControlledBrowserIds(
        new Set(
          snapshot
            .filter((activity) => activity.active)
            .map(browserActivityId)
            .filter(Boolean),
        ),
      );
      hydrated = true;
      for (const activity of pending) applyActivity(activity);
      unlisten = stop;
    });
    return () => {
      disposed = true;
      unlisten?.();
    };
  }, [nativeReady, workspace]);

  return {
    activeBrowserId,
    addBrowserTab,
    browserSessions,
    controlledBrowserIds,
    updateBrowserSession,
  };
}
